import * as React from "react";
import { useFormContext, useFieldArray } from "react-hook-form";
import { NGLabelText } from "../../../newcomponents/text/labelText";
import { FormDryRunAlert } from "./formDryRunAlert";
import { NGButton } from "../../../newcomponents/button/Button";
import { v4 as uuidv4 } from "uuid";
import { MinusCircleOutlined } from "@ant-design/icons";
import { FormNGInput } from "./components/FormNGInput";
import { FormNGSwitch } from "./components/FormNGSwitch";
import { FormNGSelect } from "./components/FormNGSelect";
import { DomainInput } from "./types/input";

interface Props {
  id: string;
  index: number;
}
export const CreateNGAdvancedPortCors: React.FC<Props> = ({ id, index }) => {
  const { control, watch } = useFormContext<DomainInput>();

  const { fields: allowOriginFields, append: appendAllowOrigin, remove: removeAllowOrigin } = useFieldArray({
    control,
    name: `advanced.spec.ports.${index}.cors.allowOrigins` as const,
  });

  const { fields: allowMethodFields, append: appendAllowMethod, remove: removeAllowMethod } = useFieldArray({
    control,
    name: `advanced.spec.ports.${index}.cors.allowMethods` as any,
  });

  const { fields: allowHeaderFields, append: appendAllowHeader, remove: removeAllowHeader } = useFieldArray({
    control,
    name: `advanced.spec.ports.${index}.cors.allowHeaders` as any,
  });

  const { fields: exposeHeaderFields, append: appendExposeHeader, remove: removeExposeHeader } = useFieldArray({
    control,
    name: `advanced.spec.ports.${index}.cors.exposeHeaders` as any,
  });

  const configureCors = watch(`advanced.spec.ports.${index}.cors.isEnabled`);
  const allowOrigins = watch(`advanced.spec.ports.${index}.cors.allowOrigins`);

  const allowMethods = watch(`advanced.spec.ports.${index}.cors.allowMethods`);
  const allowHeaders = watch(`advanced.spec.ports.${index}.cors.allowHeaders`);
  const exposeHeaders = watch(`advanced.spec.ports.${index}.cors.exposeHeaders`);

  function addAllowOrigin() {
    appendAllowOrigin({ id: uuidv4(), type: "exact", value: "" });
  }

  function addAllowMethod() {
    appendAllowMethod("");
  }

  function addAllowHeader() {
    appendAllowHeader("");
  }

  function addExposeHeader() {
    appendExposeHeader("");
  }

  return (
    <div key={`port-cors-${id}`} className="mb-8">
      <div className="mb-2">
        <NGLabelText className="cursor-default">CORS</NGLabelText>
      </div>
      <div className="mb-4">
        <FormNGSwitch name={`advanced.spec.ports.${index}.cors.isEnabled` as const} control={control}>
          <NGLabelText>Configure CORS</NGLabelText>
        </FormNGSwitch>
      </div>
      <FormDryRunAlert path={`spec.ports[${index}].cors`} />
      {configureCors ? (
        <>
          <div className="mb-4">
            <FormNGSwitch name={`advanced.spec.ports.${index}.cors.allowCredentials` as const} control={control}>
              <NGLabelText>Allow Credentials</NGLabelText>
            </FormNGSwitch>
          </div>
          <FormDryRunAlert path={`spec.ports[${index}].cors.allowCredentials`} />
          <div className="mb-8">
            <div className="flex items-center">
              <NGLabelText className="cursor-default">Max Age</NGLabelText>
            </div>
            <FormNGInput
              name={`advanced.spec.ports.${index}.cors.maxAge` as const}
              control={control}
              style={{ minWidth: 450, maxWidth: 450 }}
            />
            <FormDryRunAlert path={`spec.ports[${index}].cors.maxAge`} />
          </div>
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-2">
              <NGLabelText className="cursor-default">Allow Origins</NGLabelText>
              <NGButton
                variant="primary"
                size={"small"}
                onClick={addAllowOrigin}
                disabled={allowOrigins.some((ao) => !ao.value)}
              >
                Add
              </NGButton>
            </div>
            {allowOrigins.length < 1 ? <div>No Items</div> : null}
            {allowOriginFields.map((field, allowOriginIndex) => {
              return (
                <div key={field.id} className="mb-1">
                  <div className="flex items-center gap-2">
                    <FormNGSelect
                      name={`advanced.spec.ports.${index}.cors.allowOrigins.${allowOriginIndex}.type` as any}
                      control={control}
                      options={[
                        { label: "Exact", value: "exact" },
                        { label: "Regex", value: "regex" },
                      ]}
                      style={{ minWidth: 100, maxWidth: 100 }}
                    />
                    <FormNGInput
                      name={`advanced.spec.ports.${index}.cors.allowOrigins.${allowOriginIndex}.value` as any}
                      control={control}
                      style={{ minWidth: 342, maxWidth: 342 }}
                    />
                    <MinusCircleOutlined className="color-danger" onClick={() => removeAllowOrigin(allowOriginIndex)} />
                  </div>
                </div>
              );
            })}
            <FormDryRunAlert
              path={`spec.ports[${index}].cors.allowOrigins`}
              paths={[
                ...allowOrigins.map((_, i) => `spec.ports[${index}].cors.allowOrigins[${i}]`),
                ...allowOrigins.map((_, i) => `spec.ports[${index}].cors.allowOrigins[${i}].exact`),
                ...allowOrigins.map((_, i) => `spec.ports[${index}].cors.allowOrigins[${i}].regex`),
              ]}
            />
          </div>
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-2">
              <NGLabelText className="cursor-default">Allow Methods</NGLabelText>
              <NGButton
                variant="primary"
                size={"small"}
                onClick={addAllowMethod}
                disabled={allowMethods.some((v) => !v)}
              >
                Add
              </NGButton>
            </div>
            {allowMethods.length < 1 ? <div>No Items</div> : null}
            {allowMethodFields.map((field, allowMethodIndex) => {
              return (
                <div key={field.id} className="mb-1">
                  <div className="flex items-center gap-2">
                    <FormNGInput
                      name={`advanced.spec.ports.${index}.cors.allowMethods.${allowMethodIndex}` as any}
                      control={control}
                      style={{ minWidth: 450, maxWidth: 450 }}
                    />
                    <MinusCircleOutlined className="color-danger" onClick={() => removeAllowMethod(allowMethodIndex)} />
                  </div>
                </div>
              );
            })}
            <FormDryRunAlert
              path={`spec.ports[${index}].cors.allowMethods`}
              paths={allowMethods.map((_, i) => `spec.ports[${index}].cors.allowMethods[${i}]`)}
            />
          </div>
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-2">
              <NGLabelText className="cursor-default">Allow Headers</NGLabelText>
              <NGButton
                variant="primary"
                size={"small"}
                onClick={addAllowHeader}
                disabled={allowHeaders.some((v) => !v)}
              >
                Add
              </NGButton>
            </div>
            {allowHeaders.length < 1 ? <div>No Items</div> : null}
            {allowHeaderFields.map((field, allowHeaderIndex) => {
              return (
                <div key={field.id} className="mb-1">
                  <div className="flex items-center gap-2">
                    <FormNGInput
                      name={`advanced.spec.ports.${index}.cors.allowHeaders.${allowHeaderIndex}` as any}
                      control={control}
                      style={{ minWidth: 450, maxWidth: 450 }}
                    />
                    <MinusCircleOutlined className="color-danger" onClick={() => removeAllowHeader(allowHeaderIndex)} />
                  </div>
                </div>
              );
            })}
            <FormDryRunAlert
              path={`spec.ports[${index}].cors.allowHeaders`}
              paths={allowHeaders.map((_, i) => `spec.ports[${index}].cors.allowHeaders[${i}]`)}
            />
          </div>
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-2">
              <NGLabelText className="cursor-default">Expose Headers</NGLabelText>
              <NGButton
                variant="primary"
                size={"small"}
                onClick={addExposeHeader}
                disabled={exposeHeaders.some((v) => !v)}
              >
                Add
              </NGButton>
            </div>
            {exposeHeaders.length < 1 ? <div>No Items</div> : null}
            {exposeHeaderFields.map((field, exposeHeaderIndex) => {
              return (
                <div key={field.id} className="mb-1">
                  <div className="flex items-center gap-2">
                    <FormNGInput
                      name={`advanced.spec.ports.${index}.cors.exposeHeaders.${exposeHeaderIndex}` as any}
                      control={control}
                      style={{ minWidth: 450, maxWidth: 450 }}
                    />
                    <MinusCircleOutlined
                      className="color-danger"
                      onClick={() => removeExposeHeader(exposeHeaderIndex)}
                    />
                  </div>
                </div>
              );
            })}
            <FormDryRunAlert
              path={`spec.ports[${index}].cors.exposeHeaders`}
              paths={exposeHeaders.map((_, i) => `spec.ports[${index}].cors.exposeHeaders[${i}]`)}
            />
          </div>
        </>
      ) : null}
    </div>
  );
};
